import { Center, Loader } from '@mantine/core';
import {
    DeviceUnsupportedError,
    DisconnectReason,
    Room,
    RoomEvent,
    type RoomOptions,
    type VideoCaptureOptions,
    type VideoCodec,
    VideoPresets,
} from 'livekit-client';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { GuestLobby } from './guest-lobby';
import { notifyDeviceStartError } from './media-errors';
import { MeetingNotFound } from './meeting-not-found';
import { type PreJoinChoices, PreJoin } from './prejoin/pre-join';
import { ConferenceView } from '@/conference/conference-view';
import { fetchMeeting, type MeetingFeatures, MeetingFeaturesContext, NO_FEATURES } from '@/conference/meeting-features';
import { useLowCPUOptimizer } from '@/conference/use-low-cpu-optimizer';
import { usePageLeaveDisconnect } from '@/conference/use-page-leave-disconnect';
import { useSetupE2EE } from '@/conference/use-setup-e2ee';
import { i18n } from '@/i18n';
import { decodePassphrase } from '@/shared/client-utils';
import { notifyError } from '@/shared/notify';
import { type ConnectionDetails, isVideoCodec } from '@/shared/types';
import { useSessionUser } from '@/shared/use-session-user';

type RoomOptionsInput = { hq: boolean; codec: VideoCodec; singlePeerConnection: boolean };

export function RoomPage() {
    const { roomName = '' } = useParams();
    const [searchParams] = useSearchParams();
    const region = searchParams.get('region') ?? undefined;
    const codecParam = searchParams.get('codec');
    const options: RoomOptionsInput = {
        codec: codecParam && isVideoCodec(codecParam) ? codecParam : 'vp9',
        hq: searchParams.get('hq') === 'true',
        singlePeerConnection: searchParams.get('singlePC') !== 'false',
    };

    const user = useSessionUser();
    const [preJoinChoices, setPreJoinChoices] = useState<PreJoinChoices>();
    const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails>();
    const [meetingStatus, setMeetingStatus] = useState<'unknown' | 'found' | 'missing' | 'guestsClosed'>('unknown');
    const [features, setFeatures] = useState<MeetingFeatures>(NO_FEATURES);
    const [inLobby, setInLobby] = useState(false);
    const isGuest = user?.kind === 'guest';

    useEffect(() => {
        if (!isGuest) return;
        const controller = new AbortController();
        fetchMeeting(roomName, controller.signal)
            .then((meeting) => {
                if (!meeting) {
                    setMeetingStatus('missing');
                    return;
                }
                setMeetingStatus(meeting.features.guests ? 'found' : 'guestsClosed');
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) return;
                console.error(error);
                setMeetingStatus('found');
            });
        return () => {
            controller.abort();
        };
    }, [isGuest, roomName]);

    const connect = useCallback(
        async (values: PreJoinChoices) => {
            const url = new URL('/api/connection-details', globalThis.location.origin);
            url.searchParams.append('roomName', roomName);
            url.searchParams.append('participantName', values.username);
            if (region) url.searchParams.append('region', region);
            try {
                const response = await fetch(url);
                if (response.status === 404) {
                    setMeetingStatus('missing');
                    return;
                }
                if (response.status === 403 && isGuest) {
                    const denial = (await response.json().catch(() => null)) as { error?: string } | null;
                    if (denial?.error === 'Feature disabled') setMeetingStatus('guestsClosed');
                    else setInLobby(true);
                    return;
                }
                if (!response.ok) throw new Error(await response.text());
                const details = (await response.json()) as ConnectionDetails;
                const meeting = await fetchMeeting(roomName).catch((error: unknown) => {
                    console.error(error);
                    return null;
                });
                setFeatures(meeting?.features ?? NO_FEATURES);
                setConnectionDetails(details);
                setInLobby(false);
            } catch (error) {
                setPreJoinChoices(undefined);
                setInLobby(false);
                notifyError(i18n.t('prejoin:errors.connect'), error);
            }
        },
        [roomName, region, isGuest]
    );

    const handlePreJoinSubmit = useCallback(
        async (values: PreJoinChoices) => {
            setPreJoinChoices(values);
            if (isGuest) {
                setInLobby(true);
                return;
            }
            await connect(values);
        },
        [connect, isGuest]
    );

    if (meetingStatus === 'missing') return <MeetingNotFound roomName={roomName} />;
    if (meetingStatus === 'guestsClosed') return <MeetingNotFound reason="guestsClosed" roomName={roomName} />;

    if (inLobby && preJoinChoices && !connectionDetails) {
        return (
            <GuestLobby
                roomName={roomName}
                onAdmitted={() => void connect(preJoinChoices)}
                onCancel={() => {
                    setInLobby(false);
                    setPreJoinChoices(undefined);
                }}
                onGuestsClosed={() => {
                    setMeetingStatus('guestsClosed');
                }}
            />
        );
    }

    if (user === undefined || (isGuest && meetingStatus === 'unknown')) {
        return (
            <Center h="100%">
                <Loader />
            </Center>
        );
    }

    return (
        <main className="meet-room" data-lk-theme="default">
            {connectionDetails && preJoinChoices ? (
                <MeetingFeaturesContext value={features}>
                    <ConnectedRoom connectionDetails={connectionDetails} options={options} userChoices={preJoinChoices} />
                </MeetingFeaturesContext>
            ) : (
                <PreJoin roomName={roomName} sessionName={user?.name} onSubmit={handlePreJoinSubmit} />
            )}
        </main>
    );
}

function ConnectedRoom({
    connectionDetails,
    userChoices,
    options,
}: Readonly<{
    connectionDetails: ConnectionDetails;
    userChoices: PreJoinChoices;
    options: RoomOptionsInput;
}>) {
    const navigate = useNavigate();
    const { worker, e2eePassphrase, keyProvider } = useSetupE2EE();
    const e2eeEnabled = Boolean(e2eePassphrase && worker);
    const [e2eeKeySet, setE2eeKeySet] = useState(false);
    const [removed, setRemoved] = useState(false);
    const e2eeSetupComplete = !e2eeEnabled || e2eeKeySet;

    const [room] = useState(() => {
        let videoCodec: VideoCodec | undefined = options.codec;
        if (e2eeEnabled && (videoCodec === 'av1' || videoCodec === 'vp9')) videoCodec = undefined;
        const videoCaptureDefaults: VideoCaptureOptions = {
            deviceId: userChoices.videoDeviceId || undefined,
            resolution: options.hq ? VideoPresets.h2160 : VideoPresets.h720,
        };
        const roomOptions: RoomOptions = {
            videoCaptureDefaults,
            publishDefaults: {
                dtx: false,
                videoSimulcastLayers: options.hq
                    ? [VideoPresets.h1080, VideoPresets.h720]
                    : [VideoPresets.h540, VideoPresets.h216],
                red: !e2eeEnabled,
                videoCodec,
            },
            audioCaptureDefaults: { deviceId: userChoices.audioDeviceId || undefined },
            audioOutput: { deviceId: userChoices.audioOutputDeviceId || undefined },
            adaptiveStream: true,
            dynacast: true,
            e2ee: worker && e2eeEnabled ? { keyProvider, worker } : undefined,
            singlePeerConnection: options.singlePeerConnection,
            disconnectOnPageLeave: false,
        };
        return new Room(roomOptions);
    });

    useEffect(() => {
        if (!e2eeEnabled) return;
        keyProvider
            .setKey(decodePassphrase(e2eePassphrase))
            .then(() => room.setE2EEEnabled(true))
            .then(() => {
                setE2eeKeySet(true);
            })
            .catch((error: unknown) => {
                if (error instanceof DeviceUnsupportedError) {
                    notifyError(i18n.t('prejoin:errors.e2eeUnsupported'), error);
                } else {
                    notifyError(i18n.t('prejoin:errors.e2ee'), error);
                }
            });
    }, [e2eeEnabled, e2eePassphrase, keyProvider, room]);

    useEffect(() => {
        const handleLeave = (reason?: DisconnectReason) => {
            if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
                setRemoved(true);
                return;
            }
            void navigate('/');
        };
        const handleError = (error: Error) => {
            notifyError(i18n.t('prejoin:errors.room'), error);
        };
        const handleEncryptionError = (error: Error) => {
            notifyError(i18n.t('prejoin:errors.encryption'), error);
        };
        room.on(RoomEvent.Disconnected, handleLeave);
        room.on(RoomEvent.EncryptionError, handleEncryptionError);

        if (e2eeSetupComplete) {
            room.connect(connectionDetails.serverUrl, connectionDetails.participantToken, { autoSubscribe: true }).catch(
                handleError
            );
            if (userChoices.videoEnabled) {
                room.localParticipant.setCameraEnabled(true).catch((error: unknown) => {
                    notifyDeviceStartError('camera', error);
                });
            }
            if (userChoices.audioEnabled) {
                room.localParticipant.setMicrophoneEnabled(true).catch((error: unknown) => {
                    notifyDeviceStartError('microphone', error);
                });
            }
        }
        return () => {
            room.off(RoomEvent.Disconnected, handleLeave);
            room.off(RoomEvent.EncryptionError, handleEncryptionError);
        };
    }, [e2eeSetupComplete, room, connectionDetails, userChoices, navigate]);

    usePageLeaveDisconnect(room);

    const lowPowerMode = useLowCPUOptimizer(room);
    useEffect(() => {
        if (lowPowerMode) console.warn('Low power mode enabled');
    }, [lowPowerMode]);

    if (removed) return <MeetingNotFound reason="removed" roomName={room.name} />;
    return <ConferenceView room={room} />;
}
